/*
 * Copyright (C) Motorola Solutions, INC.
 * All Rights Reserved.
 */

import { UsbBaseService } from './usb-base.service';
import { auditTime, BehaviorSubject, filter, finalize, firstValueFrom, map, Observable, Subject, takeUntil } from 'rxjs';
import { FileUtil } from '../util/file-util';
import { tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { FirmwareReloadProgressMessage } from '../model/report-1/event/firmware-reload-message';
import { FirmwareUpdateMessage } from '../model/report-1/control/firmware-update-message';
import { ResetMessage } from '../model/report-1/control/reset-message';
import { FirmwareSizeMessage } from '../model/report-2/firmware-size-message';
import { FirmwareDataMessage } from '../model/report-2/firmware-data-message';
import { selectFirmwareUpgradeMode } from '../../../configuration/+state/configuration.selectors';
import { FirmwareUpdateComponent } from '../firmware-update/firmware-update.component';
import { Store } from '@ngrx/store';
import { ModalService } from '@msi/cobalt';
import { firmwareChecked } from '../../+state/usb.actions';
import { selectUsbFirmware } from '../../+state/usb.selectors';
import { FirmwareUpgradeMode } from '../../../configuration/model/ui-settings';

class FirmwareUpdateStatus {
    state: 'IN_PROGRESS' | 'WRITING_FLASH' | 'SUCCESS' | 'FAILED';
    progress: number;
    msg: string;
}

export abstract class FirmwareUpgradeService {
    public abstract readonly FIRMWARE: { [key: string]: string };

    public firmwareUpdateEvents$: Observable<FirmwareReloadProgressMessage>;
    public firmwareUpdateStatus$: BehaviorSubject<FirmwareUpdateStatus> = new BehaviorSubject(undefined);

    private abortFirmwareUpdateMessage = new FirmwareUpdateMessage()
        .withState(FirmwareUpdateMessage.STATE.ABORT);
    private beginFirmwareUpdateMessage = new FirmwareUpdateMessage()
        .withState(FirmwareUpdateMessage.STATE.BEGIN);
    private resetDefaultMessage = new ResetMessage()
        .withMode(ResetMessage.MODE.DEFAULT)
        .withState(ResetMessage.STATE.DEFAULT);

    private finished$: Subject<void>;

    protected constructor(
        public service: UsbBaseService,
        protected http: HttpClient,
        protected store: Store,
        protected modalService: ModalService
    ) {}

    public initialize() {
        this.store.select(selectUsbFirmware)
            .pipe(filter((v) => !!v))
            .subscribe((v) => this.checkDeviceFirmware(v).catch((e) => console.error(e)));
    }

    public async selectHostedFirmware(key: string) {
        let data = await firstValueFrom(this.http.get(this.FIRMWARE[key], { responseType: 'arraybuffer' }));
        return this.doFirmwareUpdate(data);
    }

    public async manualFirmware(file: File) {
        let data = await file.arrayBuffer();
        return this.doFirmwareUpdate(data);
    }

    public cancelFirmwareUpdate() {
        this.finished$.next();
        this.finished$.complete();
        this.service.sendReport1(this.abortFirmwareUpdateMessage)
            .then(() => console.info('Sent RTI request to cancel firmware update.'))
            .then(() => this.firmwareUpdateStatus$.next(undefined))
            .catch((e) => console.error('Failed to request firmware abort', e));
    }

    public doFirmwareUpdate(arrayBuffer: ArrayBuffer) {
        this.finished$ = new Subject<void>();
        this.firmwareUpdateEvents$ = this.service.inputReport1Events$.pipe(
            filter((message) => message instanceof FirmwareReloadProgressMessage),
            map((message) => message as FirmwareReloadProgressMessage),
            takeUntil(this.finished$)
        );
        this.firmwareUpdateStatus$.next({ state: 'IN_PROGRESS', progress: 0, msg: undefined });
        let crcAppendedBuffer = FileUtil.computeAndAppendCRC(arrayBuffer);
        let firmwareSizeMessage = new FirmwareSizeMessage()
            .withSize(arrayBuffer.byteLength + 4);
        let firmwareDataMessages = FileUtil.splitArrayBufferIntoChunks(crcAppendedBuffer, 58)
            .map((chunk, index) => new FirmwareDataMessage()
                .withSequence(index + 1)
                .withData(chunk));
        this.firmwareUpdateEvents$.pipe(
            filter((m) => m.payload1String() === 'ACK_SEQUENCE'),
            auditTime(1000),
            tap((m) => this.firmwareUpdateStatus$.next({ ...this.firmwareUpdateStatus$.value , progress: Math.trunc((m.sequence/firmwareDataMessages.length)*100) })),
            finalize(() => this.firmwareUpdateStatus$.next({ ...this.firmwareUpdateStatus$.value, progress: 100 })),
            takeUntil(this.finished$)).subscribe();
        this.firmwareUpdateEvents$.subscribe((message) => {
            switch (message.payload1String()) {
                case 'ACK_SEQUENCE':
                    if (message.sequence !== firmwareDataMessages.length) {
                        this.service
                            .sendReport2(firmwareDataMessages[message.sequence])
                            .catch(() => console.error(`Failed to send sequence ${firmwareDataMessages[message.sequence]?.sequence}`));
                    }
                    break;
                case 'UNEXPECTED_SEQUENCE':
                    console.warn('Received UNEXPECTED_SEQUENCE with sequence ' + message.sequence);
                    this.service
                        .sendReport2(firmwareDataMessages[message.sequence - 1])
                        .catch(() => console.error(`Failed to resend sequence ${firmwareDataMessages[message.sequence]?.sequence}`));
                    break;
                case 'FLASH_SUCCESS':
                    this.finished$.next();
                    this.finished$.complete();
                    this.firmwareUpdateStatus$.next({ ...this.firmwareUpdateStatus$.value, state: 'SUCCESS' });
                    this.service
                        .sendReport1(this.resetDefaultMessage)
                        .then(() => console.debug('Firmware flash succeeded. Resetting Device to finalize changes.'))
                        .catch(() => console.error('Reset failed.'));
                    break;
                case 'FLASH_FAILURE':
                    this.finished$.next();
                    this.finished$.complete();
                    this.firmwareUpdateStatus$.next({ ...this.firmwareUpdateStatus$.value, state: 'FAILED', msg: 'Flash failed.' });
                    this.service
                        .sendReport1(this.resetDefaultMessage)
                        .then(() => console.debug('Firmware flash succeeded. Resetting Device'))
                        .catch(() => console.error('Reset failed.'));
                    break;
                case 'CRC_FAIL':
                    this.finished$.next();
                    this.finished$.complete();
                    this.firmwareUpdateStatus$.next({ ...this.firmwareUpdateStatus$.value, state: 'FAILED', msg: 'CRC validation failed.' });
                    break;
                case 'IMAGE_TIMEOUT':
                    this.finished$.next();
                    this.finished$.complete();
                    this.firmwareUpdateStatus$.next({ ...this.firmwareUpdateStatus$.value, state: 'FAILED', msg: 'Image timeout.' });
                    break;
                case 'CRC_MATCH':
                    this.firmwareUpdateStatus$.next({ ...this.firmwareUpdateStatus$.value, state: 'WRITING_FLASH' });
                    break;
                default:
                    break;
            }
        });

        this.service.sendReport1(this.abortFirmwareUpdateMessage)
            .then(() => this.service.sendReport1(this.beginFirmwareUpdateMessage))
            .then(() => this.service.sendReport2(firmwareSizeMessage))
            .then(() => this.service.sendReport2(firmwareDataMessages[0]));

        return firstValueFrom(this.finished$);
    }

    public hasNewerVersion(version: string) {
        return !Object.keys(this.FIRMWARE).every((v) => version >= v);
    }

    public async checkDeviceFirmware(currentVersion: string) {
        console.debug(`${this.service.DEVICE_NAME} checking for new versions.`);
        const firmwareUpgradeMode = await firstValueFrom(this.store.select(selectFirmwareUpgradeMode).pipe(filter((mode) => !!mode)));
        if (this.hasNewerVersion(currentVersion)) {
            switch (firmwareUpgradeMode) {
                case FirmwareUpgradeMode.AUTOMATIC:
                case FirmwareUpgradeMode.PROMPTED:
                    console.debug(`New ${this.service.DEVICE_NAME} firmware version detected, prompting for upgrade.`);
                    this.modalService.open(FirmwareUpdateComponent, {
                        hasBackdrop: true,
                        disableClose: true,
                        minWidth: '25vw',
                        data: { mode: firmwareUpgradeMode }
                    });
                    return false;
                case FirmwareUpgradeMode.MANUAL:
                default:
                    console.debug(`New ${this.service.DEVICE_NAME} firmware version detected. No automated action configured.`);
                    break;
            }
        }
        this.store.dispatch(firmwareChecked({ device: this.service.DEVICE_NAME }));
        return true;
    }
}
